/**
 * PLAT-84 — pure state/validation logic for the login-by-code steps of login-form.vue.
 * Kept free of Vue/DOM so it can be unit-tested under node vitest.
 */
import { required, email } from 'gwbui/src/components/form-v2/validators';

export const STEP_LOGIN = 'login';
export const STEP_REQUEST = 'request';
export const STEP_SENT = 'sent';

/** The one email rule set: the login email field and the request-screen field both use it (AC-9). */
export const EMAIL_RULES = { required, email };

export function isSendableEmail(value) {
    // form-v2 validators are rule objects; $validator is the predicate the form itself runs.
    return Boolean(EMAIL_RULES.required.$validator(value) && EMAIL_RULES.email.$validator(value));
}

export function openRequest(state) {
    return { ...state, step: STEP_REQUEST };
}

export function backToLogin(state) {
    return { ...state, step: STEP_LOGIN };
}

export function requestSucceeded(state) {
    const { cooldownEndsAt, ...rest } = state;
    return { ...rest, step: STEP_SENT };
}

/** PLAT-87: the server refused for the per-address limit; the sent step shows [C14] until the cooling-off ends. */
export function requestLimited(state, retryAfterSeconds, nowMs) {
    return { ...state, step: STEP_SENT, cooldownEndsAt: nowMs + retryAfterSeconds * 1000 };
}

export function cooldownSecondsLeft(state, nowMs) {
    if (state.cooldownEndsAt === null || state.cooldownEndsAt === undefined) return 0;
    return Math.max(0, Math.ceil((state.cooldownEndsAt - nowMs) / 1000));
}

/** Same rounding as LoginCodeAdmission::retryAfterMinutes: whole minutes, rounded up, at least 1. */
export function cooldownMinutes(seconds) {
    return Math.max(1, Math.ceil(seconds / 60));
}

/** Only the per-address 429 is [C14]; the per-IP 429 (route throttle; body `{errors:[{status:429,...}]}`, no `status` key) and a 503 stay the generic error. */
export function isRateLimitedResponse(error) {
    return error?.response?.status === 429 && error?.response?.data?.status === 'rate_limited';
}

/**
 * PLAT-87 — what the request screen does with a failed code request. Only a per-address 429 with a usable wait
 * shows [C14]; everything else (a 503 store fault, the per-IP 429, a network error) is the generic error (AC11).
 */
export function sendFailureOutcome(error) {
    if (!isRateLimitedResponse(error)) return { kind: 'generic' };
    const seconds = Number(error.response.data.retry_after_seconds);
    return Number.isFinite(seconds) && seconds > 0 ? { kind: 'limited', seconds } : { kind: 'generic' };
}

/** PLAT-85: guest apply's per-send resend countdown (guest-apply-form.vue `countdown:`); a vitest source-scan keeps them equal. */
export const RESEND_COUNTDOWN_SECONDS = 45;

/**
 * When the resend control becomes active again. A server-told wait longer than the per-send countdown (the 3-per-10-minutes
 * window or the hourly cap is spent) is a cap wait: it is never cancelled by an expired-code error (AC-20).
 */
export function resendCountdown(waitSeconds, nowMs) {
    const wait = Number(waitSeconds);
    const serverWait = Number.isFinite(wait) && wait > 0 ? wait : 0;
    const seconds = Math.max(RESEND_COUNTDOWN_SECONDS, serverWait);
    return { endsAt: nowMs + seconds * 1000, isCapWait: serverWait > 0 };
}

export function secondsLeft(endsAt, nowMs) {
    if (endsAt === null || endsAt === undefined) return 0;
    return Math.max(0, Math.ceil((endsAt - nowMs) / 1000));
}

/** mm:ss, guest apply's `00:45` shape. */
export function formatTimer(seconds) {
    const s = Math.max(0, Math.floor(seconds));
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * PLAT-85 — what the code screen shows for a failed verify. `attempts_exhausted` uses the expired copy [C12]; anything
 * unrecognised (a 503, the per-IP 429, a network error) is the generic error.
 */
export function verifyFailureOutcome(error) {
    const response = error?.response;
    if (response?.status !== 422) return { kind: 'generic' };
    switch (response?.data?.error) {
        case 'incorrect':
            return { kind: 'incorrect' };
        case 'expired':
        case 'attempts_exhausted':
            return { kind: 'expired' };
        default:
            return { kind: 'generic' };
    }
}
